/**
 * Pomodoro Store - Zustand store for Pomodoro timer state management.
 *
 * Implements the Pomodoro timer state machine: idle → work → shortBreak/longBreak → idle.
 * This store is always initialized at app boot (not lazy) because the timer runs
 * across module boundaries.
 *
 * The store does NOT manage setInterval itself — that should be done in a React
 * effect (AppShell). The store exposes tick() for 1-second decrement with drift detection.
 *
 * @see Requirements 7.1, 7.2, 7.3, 7.5, 7.7, 7.8, 7.9, 10.3
 */

import { create } from 'zustand';
import type { PomodoroConfig, PomodoroStats } from '../types';
import { pomodoroService } from '../services/pomodoroService';

// ─── Types ───────────────────────────────────────────────────────────────────

/** Phases of the Pomodoro timer state machine. */
export type PomodoroPhase = 'idle' | 'work' | 'shortBreak' | 'longBreak';

export interface PomodoroState {
  /** Current phase of the Pomodoro state machine. */
  phase: PomodoroPhase;
  /** Remaining seconds on the current timer. */
  remainingSeconds: number;
  /** Total seconds for the current phase (used for progress calculation). */
  totalSeconds: number;
  /** Whether the timer is actively counting down. */
  isRunning: boolean;
  /** Whether the timer is paused (was running, now halted). */
  isPaused: boolean;
  /** Completed work sessions in the current cycle (0-based, resets after long break). */
  sessionCount: number;
  /** ID of the task linked to the current session, or null. */
  linkedTaskId: string | null;
  /** Title of the linked task for display purposes. */
  linkedTaskTitle: string | null;
  /** User-configurable Pomodoro durations. */
  config: PomodoroConfig;
  /** Daily statistics, loaded on demand. */
  dailyStats: PomodoroStats | null;
  /** Timestamp (ms) of the last tick for drift detection. */
  lastTickAt: number | null;
  /** Timestamp (ms) when the current phase started running. */
  startedAt: number | null;
  /** Internal session ID for pomodoroService coordination. */
  activeSessionId: string | null;
}

export interface PomodoroActions {
  /** Start a new work session, optionally linked to a task. */
  startWork: (taskId?: string, taskTitle?: string) => void;
  /** Start a break (short or long based on sessionCount). */
  startBreak: () => void;
  /** Pause the running timer. */
  pause: () => void;
  /** Resume from paused state. */
  resume: () => void;
  /** Cancel the current session and reset to idle. */
  cancel: () => Promise<void>;
  /** Called every second by setInterval — decrements timer with drift detection. */
  tick: () => void;
  /** Auto-called when timer reaches 0. Completes session and transitions phase. */
  complete: () => Promise<void>;
  /** Update the Pomodoro configuration (persisted to IndexedDB). */
  updateConfig: (config: Partial<PomodoroConfig>) => Promise<void>;
  /** Load daily stats from pomodoroService. */
  loadDailyStats: () => Promise<void>;
  /** Reset the store to idle state (e.g., on user logout). */
  reset: () => void;
}

export type PomodoroStore = PomodoroState & PomodoroActions;

// ─── Constants ───────────────────────────────────────────────────────────────

/** Default Pomodoro configuration. */
const DEFAULT_CONFIG: PomodoroConfig = {
  workDuration: 25,
  shortBreakDuration: 5,
  longBreakDuration: 15,
  sessionsBeforeLongBreak: 4,
};

/** Maximum allowed drift (in seconds) before snapping to computed value. */
const MAX_DRIFT_SECONDS = 2;

// ─── Store ───────────────────────────────────────────────────────────────────

export const usePomodoroStore = create<PomodoroStore>((set, get) => ({
  // ── State ──────────────────────────────────────────────────────────────────
  phase: 'idle',
  remainingSeconds: 0,
  totalSeconds: 0,
  isRunning: false,
  isPaused: false,
  sessionCount: 0,
  linkedTaskId: null,
  linkedTaskTitle: null,
  config: DEFAULT_CONFIG,
  dailyStats: null,
  lastTickAt: null,
  startedAt: null,
  activeSessionId: null,

  // ── Actions ────────────────────────────────────────────────────────────────

  startWork: (taskId?: string, taskTitle?: string) => {
    const { config, phase } = get();

    // Can only start work from idle or after a break completes
    if (phase !== 'idle') return;

    const totalSeconds = config.workDuration * 60;

    // Start session via pomodoroService
    const session = pomodoroService.startSession({
      taskId,
      phase: 'work',
      duration: totalSeconds,
    });

    const now = Date.now();

    set({
      phase: 'work',
      remainingSeconds: totalSeconds,
      totalSeconds,
      isRunning: true,
      isPaused: false,
      linkedTaskId: taskId ?? null,
      linkedTaskTitle: taskTitle ?? null,
      lastTickAt: now,
      startedAt: now,
      activeSessionId: session.sessionId,
    });
  },

  startBreak: () => {
    const { config, sessionCount, phase } = get();

    // Can only start break from idle (after a work session completed)
    if (phase !== 'idle') return;

    const isLongBreak = sessionCount >= config.sessionsBeforeLongBreak;
    const breakPhase: PomodoroPhase = isLongBreak ? 'longBreak' : 'shortBreak';
    const breakDuration = isLongBreak
      ? config.longBreakDuration
      : config.shortBreakDuration;
    const totalSeconds = breakDuration * 60;

    // Start a break session via pomodoroService
    const session = pomodoroService.startSession({
      phase: isLongBreak ? 'longBreak' : 'shortBreak',
      duration: totalSeconds,
    });

    const now = Date.now();

    set({
      phase: breakPhase,
      remainingSeconds: totalSeconds,
      totalSeconds,
      isRunning: true,
      isPaused: false,
      lastTickAt: now,
      startedAt: now,
      activeSessionId: session.sessionId,
    });
  },

  pause: () => {
    const { isRunning, isPaused, activeSessionId } = get();

    if (!isRunning || isPaused) return;
    if (!activeSessionId) return;

    pomodoroService.pauseSession(activeSessionId);

    set({
      isRunning: false,
      isPaused: true,
      lastTickAt: null,
    });
  },

  resume: () => {
    const { isPaused, activeSessionId } = get();

    if (!isPaused) return;
    if (!activeSessionId) return;

    pomodoroService.resumeSession(activeSessionId);

    const now = Date.now();

    set({
      isRunning: true,
      isPaused: false,
      lastTickAt: now,
      startedAt: now, // Reset startedAt for drift detection on resume
    });
  },

  cancel: async () => {
    const { activeSessionId } = get();

    if (activeSessionId) {
      await pomodoroService.cancelSession(activeSessionId);
    }

    set({
      phase: 'idle',
      remainingSeconds: 0,
      totalSeconds: 0,
      isRunning: false,
      isPaused: false,
      linkedTaskId: null,
      linkedTaskTitle: null,
      lastTickAt: null,
      startedAt: null,
      activeSessionId: null,
    });
  },

  tick: () => {
    const { isRunning, isPaused, remainingSeconds, startedAt, totalSeconds, lastTickAt } = get();

    // Only tick when actively running
    if (!isRunning || isPaused) return;
    if (remainingSeconds <= 0) return;

    const now = Date.now();

    // ─── Drift Detection ───────────────────────────────────────────────────
    // Compare actual elapsed time vs expected (1 second since lastTickAt).
    // If drift > MAX_DRIFT_SECONDS, snap to computed value based on startedAt.
    let newRemaining = remainingSeconds - 1;

    if (lastTickAt !== null && startedAt !== null) {
      const elapsedSinceLastTick = (now - lastTickAt) / 1000;
      const drift = Math.abs(elapsedSinceLastTick - 1);

      if (drift > MAX_DRIFT_SECONDS) {
        // Compute correct remaining time based on actual elapsed since phase start
        const totalElapsed = Math.floor((now - startedAt) / 1000);
        const computed = totalSeconds - totalElapsed;
        newRemaining = Math.max(0, computed);
      }
    }

    // Ensure we don't go below 0
    newRemaining = Math.max(0, newRemaining);

    set({
      remainingSeconds: newRemaining,
      lastTickAt: now,
    });

    // If timer reaches 0, trigger completion
    if (newRemaining <= 0) {
      void get().complete();
    }
  },

  complete: async () => {
    const { activeSessionId, phase, sessionCount, linkedTaskId: _linkedTaskId, config: _config } = get();

    if (!activeSessionId) return;

    // Complete the session via pomodoroService (persists to IndexedDB, emits event, updates task)
    await pomodoroService.completeSession(activeSessionId);

    // Determine next state based on current phase
    if (phase === 'work') {
      // Increment session count after work session
      const newSessionCount = sessionCount + 1;

      set({
        phase: 'idle',
        remainingSeconds: 0,
        totalSeconds: 0,
        isRunning: false,
        isPaused: false,
        lastTickAt: null,
        startedAt: null,
        activeSessionId: null,
        sessionCount: newSessionCount,
      });
    } else if (phase === 'shortBreak' || phase === 'longBreak') {
      // After a long break, reset the session count
      const newSessionCount = phase === 'longBreak' ? 0 : get().sessionCount;

      set({
        phase: 'idle',
        remainingSeconds: 0,
        totalSeconds: 0,
        isRunning: false,
        isPaused: false,
        lastTickAt: null,
        startedAt: null,
        activeSessionId: null,
        linkedTaskId: phase === 'longBreak' ? null : get().linkedTaskId,
        linkedTaskTitle: phase === 'longBreak' ? null : get().linkedTaskTitle,
        sessionCount: newSessionCount,
      });
    }
  },

  updateConfig: async (config: Partial<PomodoroConfig>) => {
    await pomodoroService.updateConfig(config);

    // Merge with current config in store
    const currentConfig = get().config;
    set({
      config: { ...currentConfig, ...config },
    });
  },

  loadDailyStats: async () => {
    const today = new Date().toISOString().split('T')[0]!;
    const stats = await pomodoroService.getDailyStats(today);
    set({ dailyStats: stats });
  },

  reset: () => {
    set({
      phase: 'idle',
      remainingSeconds: 0,
      totalSeconds: 0,
      isRunning: false,
      isPaused: false,
      sessionCount: 0,
      linkedTaskId: null,
      linkedTaskTitle: null,
      lastTickAt: null,
      startedAt: null,
      activeSessionId: null,
      dailyStats: null,
    });
  },
}));
